// User Identification - Cross-platform user identification and tracking
// Extracted from Analytics.js for better separation of concerns

import mixpanel from 'mixpanel-browser';
import Clarity from '@microsoft/clarity';
import { debugLog } from '../../utils/debugUtils';
import {
  isPreviewOrInternalDomain,
  shouldInitializeAnalytics,
} from '../../utils/environmentUtils';
import {
  logCriticalAnalyticsError,
  trackAnalyticsErrorPattern,
} from '../../utils/debugUtils';

/**
 * Unified user identification helper - identifies user across all analytics platforms
 */
export const identifyUser = email => {
  if (typeof window === 'undefined' || isPreviewOrInternalDomain()) return;

  // Early return if analytics are disabled by environment
  if (!shouldInitializeAnalytics()) return;

  // Store email for Meta Pixel PageView matching (async but non-blocking)
  storeEmailForMetaPixelMatching(email);

  // Identify in Google Analytics
  identifyUserInGoogleAnalytics(email);

  // Identify in Mixpanel
  identifyUserInMixpanel(email);

  // Identify in Clarity
  identifyUserInClarity(email);

  debugLog('🎯 User identified across all platforms:', {
    email: `${email.substring(0, 3)}***`,
  });
};

/**
 * Store email for Meta Pixel PageView matching
 */
const storeEmailForMetaPixelMatching = email => {
  import('../../utils/userDataPersistence')
    .then(({ storeUserData }) => {
      storeUserData({ email }).catch(error => {
        debugLog('Failed to store user data for Meta Pixel matching:', error);
      });
    })
    .catch(error => {
      debugLog('Failed to import userDataPersistence:', error);
    });
};

/**
 * Identify user in Google Analytics with error handling
 */
const identifyUserInGoogleAnalytics = email => {
  try {
    if (window.gtag) {
      // Set user properties in Google Analytics
      window.gtag('config', 'G-85GL0R24DZ', {
        user_id: email, // Use email as user ID for cross-device tracking
        custom_map: { email: email },
      });
      debugLog(
        '✅ Google Analytics user identified:',
        email.substring(0, 3) + '***'
      );
      // Track successful identification
      trackAnalyticsErrorPattern('google_analytics_identification', true);
    } else {
      debugLog(
        '⚠️ Google Analytics (gtag) not available for user identification:',
        email.substring(0, 3) + '***'
      );
      // Track unavailability
      trackAnalyticsErrorPattern('google_analytics_availability', false);
    }
  } catch (error) {
    debugLog('❌ Google Analytics user identification failed:', error);
    // Track failed identification
    trackAnalyticsErrorPattern('google_analytics_identification', false);

    // Send critical identification failures to Netlify function for monitoring
    logCriticalAnalyticsError(
      'google_analytics_identification_failed',
      `Google Analytics user identification failed: ${error.message}`,
      {
        error: error.message,
        stack: error.stack,
        email: email.substring(0, 3) + '***', // Sanitized email for logging
        gtagAvailable: !!window.gtag,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      },
      'error'
    );
  }
};

/**
 * Identify user in Mixpanel with error handling
 */
const identifyUserInMixpanel = email => {
  try {
    if (
      typeof mixpanel !== 'undefined' &&
      mixpanel &&
      typeof mixpanel.identify === 'function'
    ) {
      mixpanel.identify(email);
      if (typeof mixpanel.people !== 'undefined' && mixpanel.people.set) {
        mixpanel.people.set({ $email: email });
      }
      // Track successful identification
      trackAnalyticsErrorPattern('mixpanel_identification', true);
    } else {
      debugLog(
        'Mixpanel not available for user identification:',
        email.substring(0, 3) + '***'
      );
    }
  } catch (error) {
    debugLog('Mixpanel user identification failed:', error);
    // Track failed identification
    trackAnalyticsErrorPattern('mixpanel_identification', false);
  }
};

/**
 * Identify user in Clarity with error handling
 */
const identifyUserInClarity = email => {
  try {
    if (
      typeof Clarity !== 'undefined' &&
      Clarity &&
      typeof Clarity.identify === 'function'
    ) {
      // Using email as custom-id, and adding it as friendly-name
      Clarity.identify(email, undefined, undefined, email);
      debugLog('✅ Clarity user identified:', email.substring(0, 3) + '***');
      // Track successful identification
      trackAnalyticsErrorPattern('clarity_identification', true);
    } else {
      debugLog(
        '⚠️ Clarity not available for user identification:',
        email.substring(0, 3) + '***'
      );
      // Track unavailability
      trackAnalyticsErrorPattern('clarity_availability', false);
    }
  } catch (error) {
    debugLog('❌ Clarity user identification failed:', error);
    // Track failed identification
    trackAnalyticsErrorPattern('clarity_identification', false);

    // Send critical identification failures to Netlify function for monitoring
    logCriticalAnalyticsError(
      'clarity_identification_failed',
      `Clarity user identification failed: ${error.message}`,
      {
        error: error.message,
        stack: error.stack,
        email: email.substring(0, 3) + '***', // Sanitized email for logging
        clarityAvailable: typeof Clarity !== 'undefined',
        clarityObject: !!Clarity,
        hasIdentifyMethod: Clarity && typeof Clarity.identify === 'function',
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      },
      'warning' // Lower severity since Clarity is less critical for conversion tracking
    );
  }
};
